import React, { useEffect } from "react";
import { Modal, Typography, Form, DatePicker, Upload, Button, Radio, Table } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import PrimaryButton from "../Forms/PrimaryButton";
import { formRules, orderStatus } from "../../constant";
import { useGlobalModal } from "../../context/SuccessModalProvider";
import { roundDecimal, showAPC } from "../../utils";
import DataTable from "../Tables/Datatable";

const { Title } = Typography;

export default function DispatchModal({ open, onClose, handleSubmit, title = "Update to Dispatched", loading, orderData }) {
  const [form] = Form.useForm();
  const { showPopup } = useGlobalModal();

  const normFile = (e) => (Array.isArray(e) ? e : e?.fileList);

  const dispatchItems = orderData.items?.filter((item) => item.status !== orderStatus.dispatched);

  const resetAllStatus = () => {
    const items = form.getFieldValue("items") || {};

    const resetItems = Object.keys(items).reduce((acc, key) => {
      acc[key] = undefined; // or null
      return acc;
    }, {});

    form.setFieldsValue({ items: resetItems });
  };

  const dispatchColumns = [
    {
      title: "Quality Name",
      dataIndex: "short_name",
      ellipsis: true,
    },
    {
      title: "Shade",
      dataIndex: "shade_name",
      ellipsis: true,
      width: 80,
      render: (value, item) => value + showAPC(item.apc_no) || "-",
    },
    {
      title: "Quantity",
      render: (_, record) =>
        `${record.update_total_cases || record.cases || "0"} cases (${roundDecimal(record.update_total_meter || record.quantity_meters || "0")}m)`,
      ellipsis: true,
    },
    {
      title: (
        <div className="flex items-center justify-between">
          <span>Update Status</span>
          <Button type="text" danger onClick={resetAllStatus} className="text-sm">
            Reset
          </Button>
        </div>
      ),
      width: 260,
      fixed: "right",
      render: (_, record) => {
        const isDispatched = record.status === orderStatus.dispatched;
        const isPartDispatched = record.status === orderStatus.parts_dispatched;

        return (
          <Form.Item name={["items", String(record.order_item_id)]} className="mb-0 whitespace-nowrap suggested-wrapper">
            <Radio.Group>
              <Radio value={orderStatus.dispatched} className={isDispatched ? "suggested-radio" : ""}>
                Dispatched
              </Radio>
              <Radio value={orderStatus.parts_dispatched} className={isPartDispatched ? "suggested-radio" : ""}>
                Part Dispatched
              </Radio>
            </Radio.Group>
          </Form.Item>
        );
      },
    },
  ];

  const orderDataStr = JSON.stringify(orderData);

  useEffect(() => {
    form.resetFields();
  }, [orderDataStr]);

  return (
    <Modal className="form-css custom-modal" open={open} footer={null} onCancel={onClose} centered width={650}>
      <Title level={2} className="font-abhaya mb-2">
        {title}
      </Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => handleSubmit(values, form)}
        initialValues={{
          dispatch_date: dayjs(),
          // items: Object.fromEntries(
          //   orderData.items?.map((item) => [
          //     item.order_item_id,
          //     [orderStatus.dispatched, orderStatus.parts_dispatched].includes(item.status)
          //       ? item.status
          //       : undefined,
          //   ]) || []
          // ),
        }}
      >
        {
          <>
            <Title level={5}>Please add dispatch details</Title>

            {/* Dispatch Date */}
            <Form.Item label="Dispatch Date" name="dispatch_date" rules={formRules.required}>
              <DatePicker
                className="w-full h-10"
                disabledDate={(current) => {
                  return (current && current.isBefore(dayjs(orderData.created_at), "day")) || current.isAfter(dayjs().endOf("day"));
                }}
              />
            </Form.Item>

            <div className="flex justify-between gap-3">
              {/* Invoice */}
              <Form.Item
                label="Upload Invoice"
                name="invoice"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={formRules.required}
                className="w-1/2"
              >
                <Upload
                  accept=".pdf"
                  maxCount={1}
                  beforeUpload={(file) => {
                    if (!file.name.endsWith(".pdf") && !file.name.endsWith(".PDF")) {
                      showPopup({
                        content: "Please upload only PDF files.",
                        className: "custom-success-modal",
                      });
                      return Upload.LIST_IGNORE;
                    }
                    return false;
                  }}
                >
                  <Button className="h-10" icon={<UploadOutlined />}>
                    Upload Invoice Details
                  </Button>
                </Upload>
              </Form.Item>

              {/* Transport */}
              <Form.Item
                label="Upload Transport Details"
                name="transport"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={formRules.required}
                className="w-1/2"
              >
                <Upload
                  accept=".pdf"
                  maxCount={1}
                  beforeUpload={(file) => {
                    if (!file.name.endsWith(".pdf") && !file.name.endsWith(".PDF")) {
                      showPopup({
                        content: "Please upload only PDF files.",
                        className: "custom-success-modal",
                      });
                      return Upload.LIST_IGNORE;
                    }
                    return false;
                  }}
                >
                  <Button className="h-10" icon={<UploadOutlined />}>
                    Upload Transport Details
                  </Button>
                </Upload>
              </Form.Item>
            </div>
          </>
        }

        <Title level={5} className="mt-2">
          Select items to dispatch
        </Title>

        <Table
          rowKey="order_item_id"
          columns={dispatchColumns}
          dataSource={dispatchItems}
          pagination={false}
          size="middle"
          className="mb-3"
          rootClassName="transparent-table"
        />

        <div className="flex justify-center mt-4">
          <PrimaryButton text="Update Status" loading={loading} type="submit" className="me-[30px]" />
        </div>
      </Form>
    </Modal>
  );
}
